import {
  Container,
  Graphics,
  Rectangle,
  Text,
  type FederatedPointerEvent,
  type FederatedWheelEvent
} from 'pixi.js';
import type { BacklogEntry } from '../app/types';
import { Button } from './Button';
import { Panel } from './Panel';
import { UI_COLORS, UI_FONT_FAMILY } from './Theme';

export interface BacklogModalOptions {
  width?: number;
  height?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  maxEntries?: number;
  entries?: readonly BacklogEntry[];
  onClose?: () => void;
}

export interface BacklogModalState {
  visible: boolean;
  entryCount: number;
  scrollOffset: number;
}

const DEFAULT_MODAL_WIDTH = 900;
const DEFAULT_MODAL_HEIGHT = 600;
const MIN_MODAL_WIDTH = 288;
const MIN_MODAL_HEIGHT = 320;
const DEFAULT_MAX_ENTRIES = 200;
const MAX_VISIBLE_ENTRY_COUNT = 5;
const DESKTOP_ENTRY_HEIGHT = 96;
const COMPACT_ENTRY_HEIGHT = 112;
const BODY_Y = 28;
const CONTROLS_BOTTOM_OFFSET = 150;
const CONTENT_X = 8;
const COMPACT_WIDTH_THRESHOLD = 560;
const WHEEL_SCROLL_MULTIPLIER = 0.35;
const WHEEL_SCROLL_MIN_DISTANCE = 12;
const WHEEL_SCROLL_MAX_DISTANCE = 64;

export class BacklogModal extends Container {
  private readonly overlay = new Graphics();
  private readonly panel: Panel;
  private readonly entryLayer = new Container();
  private readonly emptyText = new Text({
    text: 'まだログはありません。',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 24,
      fontWeight: '700'
    }
  });
  private readonly upButton = new Button({
    label: '上へ',
    width: 132,
    height: 48,
    variant: 'ghost',
    onPress: () => this.scrollBy(-1)
  });
  private readonly downButton = new Button({
    label: '下へ',
    width: 132,
    height: 48,
    variant: 'ghost',
    onPress: () => this.scrollBy(1)
  });
  private readonly closeButton = new Button({
    label: '閉じる',
    width: 150,
    height: 48,
    variant: 'primary',
    onPress: () => this.handleClose()
  });
  private readonly entryRows: Array<{
    separator: Graphics;
    speakerText: Text;
    bodyText: Text;
    bodyMask: Graphics;
  }> = [];

  private readonly preferredWidth: number;
  private readonly preferredHeight: number;
  private readonly maxEntries: number;
  private modalWidth: number;
  private modalHeight: number;
  private viewportWidth: number;
  private viewportHeight: number;
  private entries: BacklogEntry[] = [];
  private scrollOffset = 0;
  private wheelScrollDistance = 0;
  private lastWheelDirection = 0;
  private onClose?: () => void;

  public constructor(options: BacklogModalOptions = {}) {
    super();
    this.preferredWidth = normalizeDimension(options.width, DEFAULT_MODAL_WIDTH);
    this.preferredHeight = normalizeDimension(options.height, DEFAULT_MODAL_HEIGHT);
    this.maxEntries = normalizeMaxEntries(options.maxEntries);
    this.modalWidth = this.preferredWidth;
    this.modalHeight = this.preferredHeight;
    this.viewportWidth = options.viewportWidth ?? 1280;
    this.viewportHeight = options.viewportHeight ?? 720;
    this.onClose = options.onClose;
    this.panel = new Panel({
      width: this.modalWidth,
      height: this.modalHeight,
      title: 'バックログ'
    });

    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'default';
    this.overlay.on('pointerdown', stopInputPropagation);
    this.overlay.on('pointerup', stopInputPropagation);
    this.overlay.on('pointertap', this.handleOverlayTap);
    this.panel.eventMode = 'static';
    this.panel.cursor = 'default';
    this.panel.on('pointerdown', stopInputPropagation);
    this.panel.on('pointerup', stopInputPropagation);
    this.panel.on('pointertap', stopInputPropagation);
    this.panel.on('wheel', this.handleWheel);
    for (const button of [this.upButton, this.downButton, this.closeButton]) {
      button.on('pointerdown', stopInputPropagation);
      button.on('pointerup', stopInputPropagation);
      button.on('pointertap', stopInputPropagation);
    }

    this.addChild(this.overlay);
    this.addChild(this.panel);
    this.panel.content.addChild(this.entryLayer);
    this.panel.content.addChild(this.emptyText);
    this.panel.content.addChild(this.upButton);
    this.panel.content.addChild(this.downButton);
    this.panel.content.addChild(this.closeButton);

    this.createEntryRows();
    this.setEntries(options.entries ?? []);
    this.draw();
    this.hide();
  }

  public setEntries(entries: readonly BacklogEntry[]): void {
    this.entries = normalizeEntries(entries, this.maxEntries);
    this.scrollToLatest();
    this.renderEntries();
  }

  public appendEntry(entry: BacklogEntry): void {
    this.entries = [...this.entries, cloneEntry(entry)].slice(-this.maxEntries);
    this.scrollToLatest();
    this.renderEntries();
  }

  public setViewportSize(width: number, height: number): void {
    this.viewportWidth = normalizeDimension(width, this.viewportWidth);
    this.viewportHeight = normalizeDimension(height, this.viewportHeight);
    this.draw();
  }

  public setOnClose(onClose: (() => void) | undefined): void {
    this.onClose = onClose;
  }

  public show(): void {
    this.visible = true;
  }

  public hide(): void {
    this.visible = false;
  }

  public getState(): BacklogModalState {
    return {
      visible: this.visible,
      entryCount: this.entries.length,
      scrollOffset: this.scrollOffset
    };
  }

  private readonly handleOverlayTap = (event: FederatedPointerEvent): void => {
    stopInputPropagation(event);
    this.handleClose();
  };

  private readonly handleWheel = (event: FederatedWheelEvent): void => {
    stopInputPropagation(event);
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (delta === 0) {
      return;
    }

    const direction = Math.sign(delta);
    if (direction !== this.lastWheelDirection) {
      this.wheelScrollDistance = 0;
    }
    this.lastWheelDirection = direction;

    const distance =
      direction *
      Math.min(
        WHEEL_SCROLL_MAX_DISTANCE,
        Math.max(WHEEL_SCROLL_MIN_DISTANCE, Math.abs(delta) * WHEEL_SCROLL_MULTIPLIER)
      );
    this.wheelScrollDistance += distance;

    const stepSize = this.getEntryHeight();
    const steps = Math.trunc(this.wheelScrollDistance / stepSize);
    if (steps === 0) {
      return;
    }

    const previousOffset = this.scrollOffset;
    this.scrollBy(steps);
    this.wheelScrollDistance =
      this.scrollOffset === previousOffset ? 0 : this.wheelScrollDistance - steps * stepSize;
  };

  private handleClose(): void {
    this.hide();
    this.onClose?.();
  }

  private scrollBy(delta: number): void {
    this.scrollOffset = Math.min(Math.max(this.scrollOffset + delta, 0), this.getMaxScrollOffset());
    this.renderEntries();
  }

  private createEntryRows(): void {
    for (let index = 0; index < MAX_VISIBLE_ENTRY_COUNT; index += 1) {
      const separator = new Graphics();
      const speakerText = new Text({
        text: '',
        style: {
          fill: UI_COLORS.gold,
          fontFamily: UI_FONT_FAMILY,
          fontSize: 18,
          fontWeight: '800'
        }
      });
      const bodyText = new Text({
        text: '',
        style: {
          fill: UI_COLORS.ink,
          fontFamily: UI_FONT_FAMILY,
          fontSize: 21,
          lineHeight: 29,
          wordWrap: true,
          breakWords: true
        }
      });
      const bodyMask = new Graphics();
      bodyText.mask = bodyMask;

      this.entryRows.push({ separator, speakerText, bodyText, bodyMask });
      this.entryLayer.addChild(separator);
      this.entryLayer.addChild(speakerText);
      this.entryLayer.addChild(bodyText);
      this.entryLayer.addChild(bodyMask);
    }
  }

  private renderEntries(): void {
    this.emptyText.visible = this.entries.length === 0;
    const contentWidth = this.getContentWidth();
    const visibleEntryCount = this.getVisibleEntryCount();
    const entryHeight = this.getEntryHeight();
    const compact = this.isCompactLayout();
    const speakerFontSize = compact ? 16 : 18;
    const bodyFontSize = compact ? 18 : 21;
    const bodyLineHeight = compact ? 26 : 29;

    for (const [index, row] of this.entryRows.entries()) {
      if (index >= visibleEntryCount) {
        this.hideRow(row);
        continue;
      }

      const entry = this.entries[this.scrollOffset + index];
      const y = index * entryHeight;
      row.separator.clear();
      row.separator.rect(0, y + entryHeight - 6, contentWidth, 1).fill({
        color: UI_COLORS.gold,
        alpha: 0.22
      });
      row.separator.visible = Boolean(entry);
      row.speakerText.visible = Boolean(entry);
      row.bodyText.visible = Boolean(entry);
      row.bodyMask.clear();
      row.bodyMask.visible = Boolean(entry);

      if (!entry) {
        row.speakerText.text = '';
        row.bodyText.text = '';
        continue;
      }

      row.speakerText.text = formatSpeaker(entry);
      row.speakerText.style.fontSize = speakerFontSize;
      row.speakerText.position.set(0, y);
      fitTextToWidth(row.speakerText, contentWidth, speakerFontSize, 13);

      row.bodyText.text = entry.text;
      row.bodyText.style.fontSize = bodyFontSize;
      row.bodyText.style.lineHeight = bodyLineHeight;
      row.bodyText.style.wordWrapWidth = contentWidth;
      row.bodyText.position.set(0, y + BODY_Y);
      row.bodyMask
        .rect(0, y + BODY_Y - 2, contentWidth, Math.max(1, entryHeight - BODY_Y - 12))
        .fill(0xffffff);
    }

    this.updateButtonStates();
  }

  private draw(): void {
    this.resizeModalToViewport();
    this.overlay.clear();
    this.overlay.rect(0, 0, this.viewportWidth, this.viewportHeight).fill({
      color: UI_COLORS.black,
      alpha: 0.68
    });
    this.panel.position.set(
      Math.round((this.viewportWidth - this.modalWidth) / 2),
      Math.round((this.viewportHeight - this.modalHeight) / 2)
    );
    this.layout();
    this.renderEntries();
  }

  private layout(): void {
    const contentWidth = this.getContentWidth();
    const controlsY = this.getControlsY();
    const compact = this.isCompactLayout();
    const gap = contentWidth < 300 ? 6 : compact ? 8 : 16;
    const buttonHeight = compact ? 44 : 48;
    const closeWidth = clamp(
      Math.round((contentWidth - gap * 2) * 0.42),
      compact ? 72 : 112,
      compact ? 104 : 150
    );
    const navWidth = Math.min(
      compact ? 64 : 132,
      Math.max(42, Math.floor((contentWidth - closeWidth - gap * 2) / 2))
    );

    this.entryLayer.position.set(CONTENT_X, 0);
    this.emptyText.style.wordWrapWidth = contentWidth;
    this.emptyText.position.set(
      CONTENT_X,
      Math.max(34, Math.round(this.getEntryAreaHeight() * 0.4))
    );
    this.upButton.setLabel(compact ? '上' : '上へ');
    this.downButton.setLabel(compact ? '下' : '下へ');
    this.upButton.setSize(navWidth, buttonHeight);
    this.downButton.setSize(navWidth, buttonHeight);
    this.closeButton.setSize(closeWidth, buttonHeight);
    this.upButton.position.set(CONTENT_X, controlsY);
    this.downButton.position.set(CONTENT_X + navWidth + gap, controlsY);
    this.closeButton.position.set(CONTENT_X + contentWidth - closeWidth, controlsY);
  }

  private updateButtonStates(): void {
    this.upButton.setDisabled(this.scrollOffset <= 0);
    this.downButton.setDisabled(this.scrollOffset >= this.getMaxScrollOffset());
  }

  private resizeModalToViewport(): void {
    this.modalWidth = fitDimension(
      this.preferredWidth,
      this.viewportWidth,
      getMarginX(this.viewportWidth),
      MIN_MODAL_WIDTH
    );
    this.modalHeight = fitDimension(
      this.preferredHeight,
      this.viewportHeight,
      getMarginY(this.viewportHeight),
      MIN_MODAL_HEIGHT
    );
    this.panel.setSize(this.modalWidth, this.modalHeight);
    this.panel.hitArea = new Rectangle(0, 0, this.modalWidth, this.modalHeight);
    this.scrollOffset = Math.min(this.scrollOffset, this.getMaxScrollOffset());
  }

  private scrollToLatest(): void {
    this.scrollOffset = this.getMaxScrollOffset();
    this.wheelScrollDistance = 0;
    this.lastWheelDirection = 0;
  }

  private getVisibleEntryCount(): number {
    return Math.max(
      1,
      Math.min(
        MAX_VISIBLE_ENTRY_COUNT,
        Math.floor(this.getEntryAreaHeight() / this.getEntryHeight())
      )
    );
  }

  private getMaxScrollOffset(): number {
    return Math.max(0, this.entries.length - this.getVisibleEntryCount());
  }

  private getEntryAreaHeight(): number {
    return Math.max(1, this.getControlsY() - 18);
  }

  private getControlsY(): number {
    return Math.max(118, this.modalHeight - CONTROLS_BOTTOM_OFFSET);
  }

  private getEntryHeight(): number {
    return this.isCompactLayout() ? COMPACT_ENTRY_HEIGHT : DESKTOP_ENTRY_HEIGHT;
  }

  private getContentWidth(): number {
    return Math.max(1, this.modalWidth - 72);
  }

  private isCompactLayout(): boolean {
    return this.modalWidth < COMPACT_WIDTH_THRESHOLD;
  }

  private hideRow(row: (typeof this.entryRows)[number]): void {
    row.separator.clear();
    row.bodyMask.clear();
    row.separator.visible = false;
    row.speakerText.visible = false;
    row.bodyText.visible = false;
    row.bodyMask.visible = false;
    row.speakerText.text = '';
    row.bodyText.text = '';
  }
}

function cloneEntry(entry: BacklogEntry): BacklogEntry {
  return { ...entry };
}

function normalizeEntries(entries: readonly BacklogEntry[], maxEntries: number): BacklogEntry[] {
  return entries.map(cloneEntry).slice(-maxEntries);
}

function formatSpeaker(entry: BacklogEntry): string {
  return entry.speaker && entry.speaker.length > 0 ? entry.speaker : '地の文';
}

function normalizeDimension(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(1, Math.round(value));
}

function normalizeMaxEntries(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return DEFAULT_MAX_ENTRIES;
  }

  return Math.max(1, Math.floor(value));
}

function fitDimension(preferred: number, viewport: number, margin: number, min: number): number {
  const available = Math.max(1, viewport - margin * 2);
  const lowerBound = Math.min(min, available);

  return clamp(preferred, lowerBound, available);
}

function getMarginX(viewportWidth: number): number {
  return viewportWidth < 640 ? 16 : 48;
}

function getMarginY(viewportHeight: number): number {
  return viewportHeight < 520 ? 14 : 36;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function fitTextToWidth(
  text: Text,
  maxWidth: number,
  baseFontSize: number,
  minFontSize: number
): void {
  text.style.fontSize = baseFontSize;

  while (text.width > maxWidth && Number(text.style.fontSize) > minFontSize) {
    text.style.fontSize = Number(text.style.fontSize) - 1;
  }
}

function stopInputPropagation(event: FederatedPointerEvent | FederatedWheelEvent): void {
  event.stopPropagation();
  event.preventDefault();

  if (event.nativeEvent instanceof Event) {
    event.nativeEvent.stopPropagation();
  }
}
